import { type FormEvent, Fragment, type ReactNode, useRef, useState } from 'react';
import { DESC, navFor, SEC_ORDER, SECTION } from '../../app/nav.js';
import { useLogout, useSession } from '../../app/session.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { useConfirm } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Sec } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { fv } from '../../lib/form.js';
import { initials } from '../../lib/format.js';

// Profil & Menu (port A.views.akun / A.views.menu v1 di admin.js).

/** Baris menu (v1 mi()). go = tujuan; onClick untuk aksi. */
export function MenuItem({
  go,
  onClick,
  ic,
  title,
  sub = '',
  cls = '',
}: {
  go?: string;
  onClick?: () => void;
  ic: IconName;
  title: ReactNode;
  sub?: ReactNode;
  cls?: string;
}) {
  return (
    <div className={`li click mi ${cls}`} data-go={go} onClick={onClick}>
      <span className="ic">
        <Icon n={ic} />
      </span>
      <div className="body">
        <b>{title}</b>
        {sub ? <span>{sub}</span> : null}
      </div>
      <Icon n="chev" cls="i-sm chev" />
    </div>
  );
}

/** username: kolom tersembunyi agar pengelola password browser tahu akun mana yang diganti (aksesibilitas). */
export function PwFields({ username }: { username: string }) {
  return (
    <>
      <input type="text" name="username" autoComplete="username" value={username} readOnly hidden />
      <label className="f">
        Password saat ini
        <input className="inp" type="password" name="current" required autoComplete="current-password" />
      </label>
      <label className="f">
        Password baru (min. 8 karakter)
        <input className="inp" type="password" name="next" required minLength={8} autoComplete="new-password" />
      </label>
      <label className="f">
        Ulangi password baru
        <input className="inp" type="password" name="next2" required autoComplete="new-password" />
      </label>
    </>
  );
}

export async function changePw(f: HTMLFormElement) {
  const v = (n: string) => fv(f, n);
  if (v('next') !== v('next2')) throw new Error('Password baru tidak sama.');
  await api('POST', '/api/auth/password', { current: v('current'), next: v('next') });
}

export function Akun() {
  const { me, meta, isMgr, can } = useSession();
  const logout = useLogout(useConfirm());
  const toast = useToast();
  const [pwOpen, setPwOpen] = useState(false);
  const card = useRef<HTMLDivElement>(null);
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = e.currentTarget;
    try {
      await changePw(f);
      toast('Password diganti.');
      f.reset();
    } catch (err) {
      toast((err as Error).message, true);
    }
  };
  return (
    <>
      <div className="profile">
        <div className="pf-av">{initials(me.name)}</div>
        <div className="grow">
          <b>{me.name}</b>
          <span>{me.phone || `@${me.username}`}</span>
        </div>
        <span className="pill t-accent">{me.role_name || meta.roles[me.role]}</span>
      </div>
      <Sec title="Akun saya" />
      <div className="card menu-list">
        <MenuItem
          ic="key"
          title="Ganti password"
          sub="Minimal 8 karakter"
          onClick={() => {
            setPwOpen((o) => !o);
            setTimeout(() => card.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
          }}
        />
        {isMgr ? (
          can('laporan.lihat') ? (
            <MenuItem go="/laporan" ic="chart" title="Rekap & margin" sub="Omzet, margin & pertumbuhan" />
          ) : null
        ) : me.role === 'sales' ? (
          <>
            {can('spo.lihat') ? <MenuItem go="/spo" ic="doc" title="SPO saya" sub="Riwayat pesanan" /> : null}
            {can('laporan.lihat') ? <MenuItem go="/laporan" ic="chart" title="Rekap saya" sub="Penjualan & pertumbuhan" /> : null}
          </>
        ) : me.role === 'toko' ? (
          <>
            <MenuItem go="/pesanan" ic="receipt" title="Pesanan saya" sub="Status pesanan & riwayat" />
            <MenuItem go="/pesanan?tab=tagihan" ic="wallet" title="Tagihan" sub="Nota belum lunas" />
          </>
        ) : can('tugas.antar') ? (
          <>
            <MenuItem go="/tugas" ic="home" title="Hari ini" sub="Langkah & ringkasan pengiriman" />
            <MenuItem go="/panduan" ic="headset" title="Panduan" sub="Cara memakai aplikasi" />
          </>
        ) : null}
        {meta.store ? (
          <MenuItem
            go="/lacak"
            ic="pin"
            title={meta.store.name}
            sub={[meta.store.address, meta.store.area].filter(Boolean).join(' · ')}
          />
        ) : null}
        {me.role === 'owner' ? (
          <MenuItem go="/pengaturan" ic="settings" title="Pengaturan usaha" sub="Data nota & log aktivitas" />
        ) : null}
        <MenuItem go="/panduan" ic="headset" title="Panduan aplikasi" sub="Cara pakai, istilah & tanya jawab" />
        <MenuItem
          ic="search"
          title="Cari cepat"
          sub="Nota, SPO, toko, menu & panduan"
          onClick={() => document.getElementById('searchBtn')?.click()}
        />
        <MenuItem ic="logout" title="Keluar" sub="Akhiri sesi di perangkat ini" cls="danger" onClick={() => void logout()} />
      </div>
      <div className={`card${pwOpen ? '' : ' hidden'}`} id="pwCard" ref={card}>
        <div className="card-h">
          <h3>Ganti password</h3>
        </div>
        <form className="card-b stack" id="pwF" onSubmit={submit}>
          <PwFields username={me.username} />
          <button className="btn btn-primary btn-lg">Simpan password</button>
        </form>
      </div>
    </>
  );
}

export function Menu() {
  const { me, can } = useSession();
  const logout = useLogout(useConfirm());
  const groups: Record<string, [string, string, IconName][]> = {};
  for (const [k, l, i] of navFor(me.role, can)) {
    const s = SECTION[k] || 'Utama';
    groups[s] = [...(groups[s] ?? []), [k, l, i]];
  }
  const secs = SEC_ORDER.filter((s) => groups[s]).concat(Object.keys(groups).filter((s) => !SEC_ORDER.includes(s)));
  return (
    <>
      {secs.map((s) => (
        <Fragment key={s}>
          <Sec title={s} />
          <div className="card menu-list">
            {(groups[s] ?? []).map(([k, l, i]) => (
              <MenuItem key={k} go={`/${k}`} ic={i} title={l} sub={DESC[k] || ''} />
            ))}
          </div>
        </Fragment>
      ))}
      <Sec title="Akun" />
      <div className="card menu-list">
        <MenuItem go="/akun" ic="user" title="Profil & password" sub="Ganti password akun Anda" />
        <MenuItem ic="logout" title="Keluar" sub="Akhiri sesi di perangkat ini" cls="danger" onClick={() => void logout()} />
      </div>
    </>
  );
}
